import { useCallback, useEffect, useMemo, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from '@packages/utils';
import { Switch, Tooltip } from '@idaho-aeyc/ui-kit';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import {
  useGetEventParentsListQuery,
  useExportEventParentsCSVMutation,
} from 'redux/apiSlice/eventParents.slice';
import {
  useGetEventHostsQuery,
  useGetEventLocationQuery,
} from 'redux/apiSlice/events.slice';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import CardContent from 'components/Base/Layout/Content/CardContent';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import PageWrapper from 'components/Base/PageWrapper';
import { TRtkErrorType } from 'data/types/generic.types';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import {
  EEventParentsFilterParams,
  TEventParentDataUI,
} from 'data/types/eventParents.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { buildEventParentsExportBody } from 'data/convertors/eventParents.convertor';
import useGetEventFamilyColumnsData from './EventFamilyList.data';

const FirstActionWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  gap: 8px;
  .ant-col:has(&) {
    order: -1 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .icon-question {
    font-size: 24px;
    color: #656565;
  }

  .label-text {
    font-size: 16px;
    color: #262626;
    margin: 0;
  }

  .ant-switch {
    min-width: 44px;
  }
`;

const EventFamilyList = () => {
  const { eventFamilyColumns } = useGetEventFamilyColumnsData();
  const [showOnlyActive, setShowOnlyActive] = useState(() => {
    try {
      const storedValue = localStorage.getItem(
        'event_family_list_show_active_only',
      );
      return storedValue ? JSON.parse(storedValue) : false;
    } catch {
      return false;
    }
  });

  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.family.event-family-list',
  });

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TEventParentDataUI>({
    storagePath: ETableStoragePaths.EVENT_FAMILY,
    defaultColumns: eventFamilyColumns,
  });

  const { data, isFetching, isLoading, error } = useGetEventParentsListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    isActiveOnly: showOnlyActive || undefined,
  });

  const { data: locationFilters } = useGetEventLocationQuery();
  const { data: hostFilters } = useGetEventHostsQuery();
  const { data: tagsFilters } = useGetTagsListQuery(ETagType.REGISTRANT);

  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  // Row click-through is only offered when the destination (Event details) is
  // reachable for this user, so nobody gets routed into Page Not Found.
  const hasEventViewPermission = useCheckPermission(['EVENT_VIEW']);
  const isEventsEnabled = useIsFeatureEnabled('events_management');
  const isRowClickable = hasEventViewPermission && isEventsEnabled;

  const { goEventDetails } = useNavigateWithBackState();

  const navigateToEventDetails = (record: TEventParentDataUI) => {
    return {
      onClick: () =>
        goEventDetails(record.eventId, t('TITLE', 'Event Families')),
    };
  };

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EEventParentsFilterParams.LOCATION) {
          item.filters = locationFilters;
        }
        if (item.key === EEventParentsFilterParams.HOST) {
          item.filters = hostFilters;
        }
        if (item.key === 'tagIds') {
          item.filters = tagsFilters;
        }
        return item;
      }),
    );
  }, [tagsFilters, locationFilters, hostFilters]);

  const handleActiveToggle = (checked: boolean) => {
    setShowOnlyActive(checked);
    localStorage.setItem(
      'event_family_list_show_active_only',
      JSON.stringify(checked),
    );
  };

  const [exportEventParents, { isLoading: isExporting }] =
    useExportEventParentsCSVMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleColumnKeys = useCallback((): string[] => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.EVENT_FAMILY
    ] as TColumnsState[];
    if (columnsState?.length) {
      return columnsState
        .filter(col => col.show && col.key !== 'actions')
        .map(col => String(col.key));
    }
    return columns
      .filter(col => col.key !== 'actions')
      .map(col => String(col.key));
  }, [columns]);

  const handleExportAll = useCallback(async () => {
    try {
      const body = buildEventParentsExportBody({
        filters: selectedFilters,
        sortColumn,
        visibleColumnKeys: getVisibleColumnKeys(),
        isActiveOnly: showOnlyActive || undefined,
      });
      await exportEventParents(body).unwrap();
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message || 'Export failed.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportEventParents,
    selectedFilters,
    sortColumn,
    getVisibleColumnKeys,
    showOnlyActive,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (!selectedRows.selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const resourceIds = selectedRows.selectedRowsKeys.map(key => String(key));
      const body = buildEventParentsExportBody({
        filters: selectedFilters,
        sortColumn,
        visibleColumnKeys: getVisibleColumnKeys(),
        resourceIds,
        isActiveOnly: showOnlyActive || undefined,
      });
      await exportEventParents(body).unwrap();
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message || 'Export failed.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportEventParents,
    selectedRows.selectedRowsKeys,
    selectedFilters,
    sortColumn,
    getVisibleColumnKeys,
    showOnlyActive,
    showToastNotification,
  ]);

  const tableHeaderActions = useMemo(
    () => [
      <Tooltip
        key="active-filter"
        title="The active list only contains events that have not yet completed."
      >
        <FirstActionWrapper>
          <QuestionCircleOutlined className="icon-question" />
          <span className="label-text">Show only active events:</span>
          <Switch
            checked={showOnlyActive}
            onChange={handleActiveToggle}
            size="default"
            checkedChildren="Yes"
            unCheckedChildren="No"
            aria-label="Show only active events toggle"
          />
        </FirstActionWrapper>
      </Tooltip>,
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
        disabled={!data?.content?.length}
      />,
    ],
    [
      showOnlyActive,
      handleExportAll,
      handleExportSelected,
      isExporting,
      data?.content?.length,
    ],
  );

  return (
    <PageWrapper
      title={t('TITLE', 'Event Families')}
      isLoading={isLoading}
      error={error as TRtkErrorType}
      emptyWrapper={{
        isEmpty,
        alt: 'Event Family List Empty State',
        icon: EIconNames.FAMILY,
        emptyText: t('empty-view.EMPTY_TEXT', 'No event registrants found.'),
      }}
    >
      <CardContent>
        <Table<TEventParentDataUI>
          headerActions={tableHeaderActions}
          rowKey={(record: TEventParentDataUI) => record.registrantId}
          onRow={isRowClickable ? navigateToEventDetails : undefined}
          currentPage={page}
          columns={columns}
          dataSource={data?.content}
          rowClassName={isRowClickable ? 'pointer' : undefined}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          hasFilters={hasFilters}
          onFiltersReset={onFiltersReset}
          storagePath={ETableStoragePaths.EVENT_FAMILY}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </CardContent>
    </PageWrapper>
  );
};

export default EventFamilyList;
